(() => {
  let raf = 0;
  let searchaniseFallbackTimer = 0;
  const productCache = new Map();
  const hasPendingCollectionShell = document.documentElement.classList.contains('ac-searchanise-pending');

  const isSearchResultsPage = () => /^\/pages\/search-results-page\/?$/.test(window.location.pathname);

  const getCollectionWidget = () => document.querySelector(
    '.snize-search-results.snize-collection-mode, div.snize.snize-collection-mode'
  );

  const getSearchPageWidget = () => {
    if (!isSearchResultsPage()) return null;
    const widget = document.querySelector('#snize_results.snize-search-results, #snize_results.snize, #snize_results');
    if (widget) widget.classList.add('ac-snize-search-page');
    return widget;
  };

  const getWidget = () => getCollectionWidget() || getSearchPageWidget();

  const productHandleFromHref = (href) => {
    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_) {
      return '';
    }
  };

  const fetchProduct = (handle) => {
    if (!handle) return Promise.resolve(null);
    if (!productCache.has(handle)) {
      const encoded = encodeURIComponent(handle);
      const fetchNativeProduct = () => fetch(`/products/${encoded}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then((response) => response.ok ? response.json() : null).catch(() => null);

      /* This is a rendered Liquid view, not Shopify's JSON endpoint. Request it
         as HTML/text so Shopify actually renders product.ac-card-data.liquid.
         If a preview/proxy ever returns another JSON shape, fall back to the
         reliable .js product endpoint so variant count + package text still work. */
      const request = fetch(`/products/${encoded}?view=ac-card-data`, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html, */*;q=0.8' }
      })
        .then(async (response) => {
          if (!response.ok) return null;
          const text = await response.text();
          try { return JSON.parse(text); } catch (_) { return null; }
        })
        .then(async (cardData) => {
          if (cardData && Array.isArray(cardData.variants)) return cardData;

          const nativeProduct = await fetchNativeProduct();
          if (!nativeProduct) return null;

          /* Preserve Days to Maturity if the rendered view returned useful
             metadata but an unexpected/nested product payload. */
          const maturity = cardData?.days_to_maturity ?? cardData?.product?.days_to_maturity;
          if (maturity != null) nativeProduct.days_to_maturity = maturity;
          return nativeProduct;
        })
        .catch(() => fetchNativeProduct());
      productCache.set(handle, request);
    }
    return productCache.get(handle);
  };

  const cleanVariantTitle = (title) => {
    if (!title || title === 'Default Title') return '';
    return String(title)
      .replace(/\s*[–—-]\s*/g, ' · ')
      .replace(/\s*\/\s*/g, ' · ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const ensureStructure = (card) => {
    if (!card) return null;
    card.classList.add('ac-snize-card');

    const link = card.querySelector(':scope > a.snize-view-link') || card.querySelector('a.snize-view-link');
    const item = link?.querySelector('.snize-item') || card.querySelector('.snize-item');
    const info = item?.querySelector('.snize-overhidden') || card.querySelector('.snize-overhidden');
    const title = info?.querySelector('.snize-title') || card.querySelector('.snize-title');
    const price = info?.querySelector('.snize-price-list') || card.querySelector('.snize-price-list');
    if (!link || !item || !info || !title || !price) return null;

    /* Searchanise can render ratings through its review integration. Keep the
       native rating markup, but move it into a fixed AC row so all cards align. */
    let review = info.querySelector('.ac-snize-card-review');
    if (!review) {
      review = document.createElement('span');
      review.className = 'ac-snize-card-review is-placeholder';
      review.innerHTML = '&nbsp;';
      title.insertAdjacentElement('afterend', review);
    }

    const stars = card.querySelector('.stars-container');
    const ratingWrapper = stars?.closest(
      '.snize-product-rating, .snize-rating, .snize-rating-wrapper, .snize-product-rating-wrapper'
    );
    const ratingNode = ratingWrapper || stars;
    if (ratingNode && !review.contains(ratingNode)) {
      review.textContent = '';
      review.classList.remove('is-placeholder');
      review.appendChild(ratingNode);

      /* Pull an adjacent rating/review count in as well when Searchanise
         outputs it separately from the stars container. */
      const possibleCount = [
        ratingNode.nextElementSibling,
        ratingNode.parentElement?.querySelector('.snize-reviews-count'),
        ratingNode.parentElement?.querySelector('.snize-review-count'),
        ratingNode.parentElement?.querySelector('.reviews-count'),
        ratingNode.parentElement?.querySelector('.review-count')
      ].find((node) => node && node !== ratingNode && !review.contains(node));
      if (possibleCount) review.appendChild(possibleCount);
    }

    let meta = info.querySelector('.ac-snize-card-meta');
    if (!meta) {
      meta = document.createElement('span');
      meta.className = 'ac-snize-card-meta is-placeholder';
      meta.innerHTML = '&nbsp;';
    }

    let maturity = info.querySelector('.ac-snize-card-maturity');
    if (!maturity) {
      maturity = document.createElement('span');
      maturity.className = 'ac-snize-card-maturity';
      maturity.hidden = true;
    }

    let bottom = info.querySelector('.ac-snize-card-bottom');
    if (!bottom) {
      bottom = document.createElement('span');
      bottom.className = 'ac-snize-card-bottom';
      info.appendChild(bottom);
    }

    let priceBlock = bottom.querySelector('.ac-snize-card-priceblock');
    if (!priceBlock) {
      priceBlock = document.createElement('span');
      priceBlock.className = 'ac-snize-card-priceblock';
      bottom.insertBefore(priceBlock, bottom.firstChild);
    }

    let priceLine = priceBlock.querySelector('.ac-snize-card-price-line');
    if (!priceLine) {
      priceLine = document.createElement('span');
      priceLine.className = 'ac-snize-card-price-line';
      priceBlock.insertBefore(priceLine, priceBlock.firstChild);
    }
    if (!priceLine.contains(price)) priceLine.appendChild(price);
    if (!priceBlock.contains(maturity)) priceBlock.appendChild(maturity);
    if (!priceBlock.contains(meta)) priceBlock.appendChild(meta);
    if (maturity.previousElementSibling !== priceLine) priceLine.insertAdjacentElement('afterend', maturity);
    if (meta.previousElementSibling !== maturity) maturity.insertAdjacentElement('afterend', meta);

    let cta = bottom.querySelector('.ac-snize-card-cta');
    if (!cta) {
      cta = document.createElement('span');
      cta.className = 'ac-snize-card-cta';
      cta.textContent = 'Choose Options';
      cta.setAttribute('aria-hidden', 'true');
      bottom.appendChild(cta);
    }

    /* Remove the CTA left behind by older iterations, if any. */
    info.querySelectorAll(':scope > .ac-snize-card-cta').forEach((old) => old.remove());

    const href = link.getAttribute('href') || '';
    const handle = productHandleFromHref(href);
    return { card, link, info, review, maturity, meta, bottom, priceBlock, priceLine, cta, price, handle };
  };

  const enrichCard = async (parts) => {
    if (!parts || parts.card.dataset.acSnizeEnriched === 'loading' || parts.card.dataset.acSnizeEnriched === 'done') return;
    parts.card.dataset.acSnizeEnriched = 'loading';

    const product = await fetchProduct(parts.handle);
    if (!product) {
      parts.card.dataset.acSnizeEnriched = 'done';
      return;
    }

    const daysToMaturity = Number(product.days_to_maturity || 0);
    if (Number.isFinite(daysToMaturity) && daysToMaturity > 0) {
      parts.maturity.textContent = `${Math.round(daysToMaturity)} days to maturity`;
      parts.maturity.hidden = false;
    } else {
      parts.maturity.textContent = '';
      parts.maturity.hidden = true;
    }

    const variants = Array.isArray(product.variants) ? product.variants : [];
    const availableVariants = variants.filter((variant) => variant && variant.available !== false);
    const isSoldOut = product.available === false || (variants.length > 0 && availableVariants.length === 0);
    const representative = availableVariants[0] || variants[0];
    const productTitle = String(product.title || '');
    const productHandle = String(parts.handle || '');
    const isGrowingGuide = /\bgrowing\s+guide\b/i.test(productTitle) || /growing-guide/i.test(productHandle);
    const titleLooksLikeSeedProduct = /\bseeds\b/i.test(productTitle);

    /* Explicit package metadata is the source of truth for seed packets.
       A conservative title/variant-title fallback covers real seed products
       that are missing the metafield, while Growing Guides are always excluded. */
    const explicitPacketVariant = variants.find((variant) => {
      if (!variant) return false;
      const packageType = String(variant.package_type || '').trim().toLowerCase();
      const isMixMatchPacket = variant.mix_match_eligible === true || String(variant.mix_match_eligible || '').toLowerCase() === 'true';
      return packageType === 'packet' || isMixMatchPacket;
    });
    const fallbackPacketVariant = !isGrowingGuide && titleLooksLikeSeedProduct
      ? variants.find((variant) => variant && /^Packet\b/i.test(String(variant.title || '')))
      : null;
    const packetVariant = isGrowingGuide ? null : (explicitPacketVariant || fallbackPacketVariant);

    /* Availability badge sits on the product image and mirrors the CTA state. */
    const thumb = parts.card.querySelector('.snize-thumbnail-wrapper');
    let soldOutBadge = parts.card.querySelector('.ac-snize-sold-out');
    if (isSoldOut) {
      if (!soldOutBadge && thumb) {
        soldOutBadge = document.createElement('span');
        soldOutBadge.className = 'ac-snize-sold-out';
        soldOutBadge.textContent = 'Sold Out';
        thumb.appendChild(soldOutBadge);
      }
      parts.card.classList.add('is-ac-sold-out');
    } else {
      if (soldOutBadge) soldOutBadge.remove();
      parts.card.classList.remove('is-ac-sold-out');
    }
    const variantText = cleanVariantTitle(representative?.title || '');

    let metaText = '';
    if (variants.length > 1) {
      metaText = `${variants.length} sizes`;
      if (variantText) metaText += ` · ${variantText}`;
      parts.cta.textContent = 'Choose Options';
    } else if (variantText) {
      metaText = variantText;
    } else {
      parts.cta.textContent = variants.length > 1 ? 'Choose Options' : 'View Product';
    }

    if (metaText) {
      parts.meta.textContent = metaText;
      parts.meta.classList.remove('is-placeholder');
    }

    let from = parts.priceLine.querySelector('.ac-snize-card-from');
    if (variants.length > 1) {
      if (!from) {
        from = document.createElement('span');
        from.className = 'ac-snize-card-from';
        from.textContent = 'From';
        parts.priceLine.insertBefore(from, parts.price);
      }
      parts.price.classList.add('has-from-prefix');
    } else {
      if (from) from.remove();
      parts.price.classList.remove('has-from-prefix');
    }

    if (isSoldOut) {
      parts.cta.textContent = 'Sold Out';
      parts.cta.classList.remove('is-add-packet', 'is-direct-add');
      parts.cta.classList.add('is-sold-out');
      parts.cta.removeAttribute('data-ac-direct-add');
      parts.cta.removeAttribute('data-ac-variant-id');
      parts.cta.removeAttribute('role');
      parts.cta.removeAttribute('tabindex');
      parts.cta.setAttribute('aria-hidden', 'true');
    } else if (packetVariant && packetVariant.available !== false) {
      parts.cta.textContent = 'Add Packet';
      parts.cta.classList.remove('is-sold-out');
      parts.cta.classList.add('is-add-packet', 'is-direct-add');
      parts.cta.dataset.acDirectAdd = 'true';
      parts.cta.dataset.acVariantId = String(packetVariant.id || '');
      parts.cta.setAttribute('role', 'button');
      parts.cta.setAttribute('tabindex', '0');
      parts.cta.setAttribute('aria-label', `Add standard packet of ${product.title || 'product'} to cart`);
      parts.cta.removeAttribute('aria-hidden');
    } else if (variants.length <= 1 && representative) {
      parts.cta.textContent = 'Add to Cart';
      parts.cta.classList.remove('is-add-packet', 'is-sold-out');
      parts.cta.classList.add('is-direct-add');
      parts.cta.dataset.acDirectAdd = 'true';
      parts.cta.dataset.acVariantId = String(representative.id || '');
      parts.cta.setAttribute('role', 'button');
      parts.cta.setAttribute('tabindex', '0');
      parts.cta.setAttribute('aria-label', `Add ${product.title || 'product'} to cart`);
      parts.cta.removeAttribute('aria-hidden');
    } else {
      parts.cta.textContent = 'Choose Options';
      parts.cta.classList.remove('is-add-packet', 'is-sold-out', 'is-direct-add');
      parts.cta.removeAttribute('data-ac-direct-add');
      parts.cta.removeAttribute('data-ac-variant-id');
      parts.cta.removeAttribute('role');
      parts.cta.removeAttribute('tabindex');
      parts.cta.setAttribute('aria-hidden', 'true');
    }

    parts.card.dataset.acSnizeEnriched = 'done';
  };

  const addVariantToCart = async (cta) => {
    if (!cta || cta.dataset.acAdding === 'true') return;
    const variantId = Number(cta.dataset.acVariantId || 0);
    if (!variantId) return;

    const originalText = cta.textContent || 'Add to Cart';
    cta.dataset.acAdding = 'true';
    cta.classList.add('is-loading');
    cta.textContent = 'Adding…';
    cta.setAttribute('aria-disabled', 'true');

    try {
      const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
      const response = await fetch(`${root}/cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.description || result.message || 'Unable to add item to cart');

      cta.textContent = 'Added';
      document.dispatchEvent(new CustomEvent('addToCartFlyout:complete', { detail: result }));
      window.setTimeout(() => { cta.textContent = originalText; }, 1100);
    } catch (error) {
      console.error('AC Searchanise add to cart:', error);
      cta.textContent = 'Try Again';
      window.setTimeout(() => { cta.textContent = originalText; }, 1600);
    } finally {
      cta.dataset.acAdding = 'false';
      cta.classList.remove('is-loading');
      cta.removeAttribute('aria-disabled');
    }
  };

  document.addEventListener('click', (event) => {
    const cta = event.target.closest?.('.ac-snize-card-cta[data-ac-direct-add="true"]');
    if (!cta) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    addVariantToCart(cta);
  }, true);

  document.addEventListener('keydown', (event) => {
    const cta = event.target.closest?.('.ac-snize-card-cta[data-ac-direct-add="true"]');
    if (!cta || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    event.stopPropagation();
    addVariantToCart(cta);
  }, true);

  const decorateCards = (widget) => {
    if (!widget) return;
    widget.querySelectorAll('ul.snize-search-results-content.clearfix').forEach((ul) => ul.classList.remove('clearfix'));
    widget.querySelectorAll(
      '#snize-search-results-grid-mode li.snize-product, #snize-search-results-wrapper li.snize-product, li.snize-product'
    ).forEach((card) => {
      const parts = ensureStructure(card);
      if (parts) enrichCard(parts);
    });
  };

  const ensureSearchPageHeading = (widget) => {
    if (!widget || !isSearchResultsPage()) return;

    /* Searchanise initializes this page by waiting for #snize_results to be empty.
       Never inject our heading until the app has rendered its own result UI. */
    const searchaniseReady = widget.querySelector(
      '.snize-main-panel, .snize-search-results-main-content, .snize-header, li.snize-product'
    );
    if (!searchaniseReady) return;

    document.documentElement.classList.add('ac-searchanise-search-active');

    /* Hide the generic Shopify page title only on this dedicated Searchanise page. */
    document.querySelectorAll('h1').forEach((heading) => {
      if (!widget.contains(heading) && heading.textContent.trim().toLowerCase() === 'search results') {
        heading.classList.add('ac-search-results-native-title');
      }
    });

    if (widget.querySelector(':scope > .ac-snize-search-hero')) return;

    const params = new URLSearchParams(window.location.search);
    const query = (params.get('q') || '').trim();
    const hero = document.createElement('section');
    hero.className = 'ac-snize-search-hero';

    const eyebrow = document.createElement('div');
    eyebrow.className = 'ac-snize-search-eyebrow';
    eyebrow.textContent = "SEARCH SEEDS 'N SUCH";

    const title = document.createElement('h1');
    title.className = 'ac-snize-search-title';
    title.textContent = query ? `Search results for “${query}”` : 'Search Seeds & Garden Supplies';

    hero.append(eyebrow, title);
    widget.insertBefore(hero, widget.firstChild);
  };



  const setAcViewMode = (widget, mode) => {
    if (!widget) return;
    widget.classList.remove('ac-snize-view-grid', 'ac-snize-view-list', 'ac-snize-view-big');
    widget.classList.add(`ac-snize-view-${mode}`);
    widget.dataset.acViewMode = mode;
  };

  const syncMobileViewMode = (widget) => {
    if (!widget) return;

    /* Once the visitor explicitly chooses an AC view, keep it. Searchanise
       mutates its active classes several times after a click and used to
       immediately overwrite our layout again. */
    if (widget.dataset.acViewMode === 'grid' || widget.dataset.acViewMode === 'big') {
      setAcViewMode(widget, widget.dataset.acViewMode);
      return;
    }

    const controls = Array.from(widget.querySelectorAll('.snize-view-mode-icon'));
    const active = controls.find((el) => el.classList.contains('active'));
    const gridIcon = controls.find((el) => el.classList.contains('snize-grid-mode-icon'));
    const listIcon = controls.find((el) => el.classList.contains('snize-list-mode-icon'));

    if (active) {
      if (active === gridIcon || active.classList.contains('snize-grid-mode-icon')) {
        setAcViewMode(widget, 'grid');
      } else if (window.matchMedia('(max-width: 680px)').matches) {
        /* On mobile the native List icon is hidden. Any other active visible
           control is our Big Picture mode. */
        if (active !== listIcon) setAcViewMode(widget, 'big');
        else setAcViewMode(widget, 'grid');
      }
      return;
    }

    setAcViewMode(widget, 'grid');
  };

  const removeFilterCounts = (widget) => {
    if (!widget) return;

    /* Searchanise renders filter totals in dedicated count elements.
       Remove them completely so they cannot reappear after app re-renders. */
    widget.querySelectorAll('.snize-filter-variant-count').forEach((count) => count.remove());

    widget.querySelectorAll('.snize-filters-sidebar, .snize-product-filters').forEach((filters) => {
      const walker = document.createTreeWalker(filters, NodeFilter.SHOW_TEXT);

      while (walker.nextNode()) {
        const node = walker.currentNode;
        const cleaned = node.nodeValue.replace(/\s*\([\d,]+\)\s*$/g, '');

        if (cleaned !== node.nodeValue) {
          node.nodeValue = cleaned;
        }
      }
    });
  };

  const fixBestSellingLabel = (widget) => {
    if (!widget) return;

    /* Searchanise renders this sort label as "Bestselling". Keep its
       underlying sort value untouched and only normalize the visible copy. */
    widget.querySelectorAll('option').forEach((option) => {
      if (/^best\s*selling$/i.test((option.textContent || '').trim())) {
        option.textContent = 'Best Selling';
      }
    });

    const walker = document.createTreeWalker(widget, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const node = walker.currentNode;
      const value = node.nodeValue || '';
      if (/^\s*best\s*selling\s*$/i.test(value)) {
        node.nodeValue = value.replace(/best\s*selling/i, 'Best Selling');
      }
    }
  };

  const polishSearchNavigation = (widget) => {
    if (!widget || !widget.classList.contains('ac-snize-search-page')) return;

    widget.querySelectorAll('a, button').forEach((tab) => {
      const label = (tab.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (/^(categories|pages)(\s*\d+)?$/.test(label)) tab.classList.add('ac-snize-nav-tab');
    });

    widget.querySelectorAll('li:not(.snize-product)').forEach((item) => {
      if (item.querySelector('a[href*="/products/"]')) return;
      const link = item.querySelector('a[href]');
      if (!link) return;
      const href = link.getAttribute('href') || '';
      if (/\/collections\//i.test(href)) item.classList.add('ac-snize-category-result');
      if (/\/(pages|blogs)\//i.test(href)) item.classList.add('ac-snize-page-result');
    });
  };

  const sync = () => {
    raf = 0;
    const collectionWidget = getCollectionWidget();
    const searchWidget = getSearchPageWidget();
    const widget = collectionWidget || searchWidget;
    const native = document.querySelector('.collection--section.section--canonical');

    /* Apply our Searchanise structure before revealing the collection. This
       avoids the visible native -> raw Searchanise -> AC Searchanise swap. */
    ensureSearchPageHeading(searchWidget);
    fixBestSellingLabel(widget);
    polishSearchNavigation(searchWidget);
    syncMobileViewMode(widget);
    removeFilterCounts(widget);
    decorateCards(widget);

    if (collectionWidget) {
      if (native) {
        native.hidden = true;
        native.setAttribute('aria-hidden', 'true');
      }
      document.documentElement.classList.add('ac-searchanise-collection-active');
      document.documentElement.classList.remove('ac-searchanise-pending');
      if (searchaniseFallbackTimer) {
        window.clearTimeout(searchaniseFallbackTimer);
        searchaniseFallbackTimer = 0;
      }
    } else if (native && !document.documentElement.classList.contains('ac-searchanise-pending')) {
      native.hidden = false;
      native.removeAttribute('aria-hidden');
      document.documentElement.classList.remove('ac-searchanise-collection-active');
    }
  };

  const queueSync = () => {
    if (raf) return;
    raf = requestAnimationFrame(sync);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', queueSync);
  } else {
    queueSync();
  }

  /* The native Empire collection is a real fallback, but it should never be
     the first paint while Searchanise is expected. Reveal it only if the
     third-party widget genuinely fails to initialize. */
  if (hasPendingCollectionShell) {
    searchaniseFallbackTimer = window.setTimeout(() => {
      if (getCollectionWidget()) {
        queueSync();
        return;
      }
      document.documentElement.classList.remove('ac-searchanise-pending');
      document.documentElement.classList.remove('ac-searchanise-collection-active');
      const native = document.querySelector('.collection--section.section--canonical');
      if (native) {
        native.hidden = false;
        native.removeAttribute('aria-hidden');
      }
    }, 5000);
  }

  document.addEventListener('click', (event) => {
    if (!event.target || !event.target.closest) return;
    const control = event.target.closest('#snize_results .snize-view-mode-icon');
    if (!control) return;

    const widget = control.closest('#snize_results');
    if (widget) {
      const isMobile = window.matchMedia('(max-width: 680px)').matches;

      if (isMobile) {
        /* Searchanise's third view icon has changed class names between widget
           releases. Map the first visible control to Grid and the last visible
           control to Big Picture by position instead of class name. */
        const visibleControls = Array.from(widget.querySelectorAll('.snize-view-mode-icon'))
          .filter((el) => getComputedStyle(el).display !== 'none');
        const index = visibleControls.indexOf(control);
        setAcViewMode(widget, index <= 0 ? 'grid' : 'big');
      } else if (control.classList.contains('snize-grid-mode-icon')) {
        setAcViewMode(widget, 'grid');
      } else if (control.classList.contains('snize-list-mode-icon')) {
        setAcViewMode(widget, 'list');
      } else {
        setAcViewMode(widget, 'big');
      }
    }

    /* Do not re-read Searchanise's asynchronous active state here. The AC
       data attribute is the source of truth after an explicit visitor click. */
    requestAnimationFrame(queueSync);
  }, true);

  const observer = new MutationObserver(queueSync);
  observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  document.addEventListener('shopify:section:load', queueSync);
  document.addEventListener('shopify:section:unload', queueSync);
})();
